/**
 * Parent Challenge Page
 * "21 Days to Calm & Connection" - A clean, no-gamification experience
 * for parents to follow daily practices.
 * 
 * Enhanced with:
 * - Opening Insight
 * - Practice Steps
 * - Connection Tip
 * - Evening Reflection
 * - Science Snippet
 * - Daily Affirmations (with Voice Studio link)
 * - Breathing Reset link
 */

import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  ArrowLeft, 
  Heart, 
  Check, 
  ChevronRight,
  Wind,
  Loader2,
  Lightbulb,
  MessageCircle,
  Moon,
  Sparkles,
  Mic,
  ChevronDown,
  ChevronUp
} from "lucide-react";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Week themes for visual distinction - Enhanced with gradients
const WEEK_THEMES = {
  1: { 
    name: "Creating Calm", 
    color: "#4A6741", 
    lightBg: "rgba(74, 103, 65, 0.08)",
    gradient: "from-emerald-50 to-green-50",
    accentGradient: "from-[#4A6741] to-[#5A7D51]",
    borderColor: "border-emerald-200"
  },
  2: { 
    name: "Rebuilding Connection", 
    color: "#D4A373", 
    lightBg: "rgba(212, 163, 115, 0.08)",
    gradient: "from-amber-50 to-orange-50",
    accentGradient: "from-[#D4A373] to-[#E5B384]",
    borderColor: "border-amber-200"
  },
  3: { 
    name: "Becoming a Calm Anchor", 
    color: "#6366F1", 
    lightBg: "rgba(99, 102, 241, 0.08)",
    gradient: "from-indigo-50 to-violet-50",
    accentGradient: "from-[#6366F1] to-[#818CF8]",
    borderColor: "border-indigo-200"
  }
};

const ParentChallengePage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [challenge, setChallenge] = useState(null);
  const [progress, setProgress] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [reflection, setReflection] = useState("");
  const [completing, setCompleting] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    insight: true,
    practice: true,
    connection: false,
    affirmations: false,
    science: false
  });
  
  const getToken = () => localStorage.getItem("reclaim_token");
  
  const loadChallenge = useCallback(async () => {
    const token = getToken();
    if (!token) {
      toast.error("Please log in to access Parent Zone");
      navigate("/login");
      return;
    }
    
    try {
      const res = await axios.get(`${API_URL}/api/parent/challenge`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChallenge(res.data.challenge);
      setProgress(res.data.progress);
      
      if (!selectedDay) {
        setSelectedDay(res.data.progress.current_day);
      }
    } catch (err) {
      console.error("Parent challenge error:", err.response?.status, err.response?.data);
      if (err.response?.status === 403) {
        toast.error("Parent challenge not unlocked");
        navigate("/");
      } else if (err.response?.status === 401) {
        toast.error("Session expired. Please log in again.");
        navigate("/login");
      } else {
        toast.error("Failed to load challenge");
      }
    } finally {
      setLoading(false);
    }
  }, [navigate, selectedDay]);
  
  useEffect(() => {
    loadChallenge();
  }, [loadChallenge]);
  
  const handleCompleteDay = async () => {
    if (!selectedDay) return;
    
    setCompleting(true);
    try {
      await axios.post(
        `${API_URL}/api/parent/challenge/day/${selectedDay}/complete`,
        { day: selectedDay, reflection: reflection || null },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      
      toast.success("Day completed");
      setReflection("");
      await loadChallenge();
      
      if (selectedDay < 21) {
        setSelectedDay(selectedDay + 1);
      }
    } catch (err) {
      toast.error("Failed to complete day");
    } finally {
      setCompleting(false);
    }
  };
  
  const getDayContent = () => {
    if (!challenge || !selectedDay) return null;
    return challenge.days.find(d => d.day === selectedDay);
  };
  
  const isDayCompleted = (day) => {
    return progress?.completed_days?.includes(day);
  };
  
  const isDayUnlocked = (day) => {
    if (!progress) return day === 1;
    return day <= progress.current_day || isDayCompleted(day - 1);
  };
  
  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#F5F2ED] to-[#E6E2DD] flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
        >
          <Heart className="w-12 h-12 text-[#4A6741]" />
        </motion.div>
      </div>
    );
  }
  
  const dayContent = getDayContent();
  const weekTheme = dayContent ? WEEK_THEMES[dayContent.week] : WEEK_THEMES[1];
  const completedCount = progress?.completed_days?.length || 0;
  const isComplete = completedCount >= 21;
  
  // Collapsible Section Component - Enhanced with subtle colors
  const CollapsibleSection = ({ id, icon: Icon, title, children, color = "#4A6741", bgGradient = "" }) => (
    <div className={`rounded-2xl shadow-sm border overflow-hidden mb-4 ${bgGradient ? `bg-gradient-to-br ${bgGradient}` : 'bg-white'} ${bgGradient ? 'border-white/50' : 'border-[#E6E2DD]'}`}>
      <button
        onClick={() => toggleSection(id)}
        className={`w-full p-4 flex items-center justify-between transition-colors ${bgGradient ? 'hover:bg-white/30' : 'hover:bg-[#F9FAFB]'}`}
      >
        <div className="flex items-center gap-3">
          <div 
            className="w-8 h-8 rounded-lg flex items-center justify-center shadow-sm"
            style={{ backgroundColor: bgGradient ? 'rgba(255,255,255,0.7)' : `${color}15` }}
          >
            <Icon size={16} style={{ color }} />
          </div>
          <span className="font-medium text-[#292524]">{title}</span>
        </div>
        {expandedSections[id] ? (
          <ChevronUp size={18} className="text-[#78716C]" />
        ) : (
          <ChevronDown size={18} className="text-[#78716C]" />
        )}
      </button>
      <AnimatePresence>
        {expandedSections[id] && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <div className={`px-4 pb-4 pt-0 ${bgGradient ? 'bg-white/40' : ''}`}>
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FDFCFB] via-[#F5F2ED] to-[#E6E2DD]">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-gradient-to-r from-[#FDFCFB]/95 to-[#F5F2ED]/95 backdrop-blur-sm border-b border-[#E6E2DD]">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button 
              onClick={() => navigate("/")}
              className="flex items-center gap-2 text-[#78716C] hover:text-[#292524] transition-colors"
              data-testid="parent-challenge-back-btn"
            >
              <ArrowLeft size={20} />
              <span className="text-sm">Home</span>
            </button>
            
            <div className="text-center">
              <h1 className="text-sm font-medium text-[#292524]">
                21 Days to Calm & Connection
              </h1>
              <p className="text-xs text-[#78716C]">
                Day {completedCount} of 21
              </p>
            </div>
            
            <div className="w-16" />
          </div>
        </div>
      </div>
      
      <div className="container mx-auto px-4 py-6 max-w-2xl">
        {/* Completion State */}
        {isComplete && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-gradient-to-br from-[#4A6741] to-[#3D5A35] rounded-2xl p-8 text-white text-center mb-8"
          >
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-white/20 flex items-center justify-center">
              <Heart className="w-8 h-8" />
            </div>
            <h2 className="text-2xl font-bold mb-3">Journey Complete</h2>
            <p className="text-white/90 mb-2">Calm parents create calm children.</p>
            <p className="text-white/90 mb-4">Connection grows through presence.</p>
            <p className="text-white/70 text-sm">
              The skills you've practiced are now part of you.<br />
              Continue using them, and watch your relationship flourish.
            </p>
          </motion.div>
        )}
        
        {/* Day Selector - Color coded by week */}
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-sm font-medium text-[#57534E]">Select Day</span>
          </div>
          
          <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
            {[1, 2, 3].map(week => {
              const wTheme = WEEK_THEMES[week];
              return (
                <div key={week} className={`flex gap-1.5 p-2 rounded-xl bg-gradient-to-br ${wTheme.gradient}`}>
                  {challenge?.days
                    .filter(d => d.week === week)
                    .map(d => {
                      const isUnlocked = isDayUnlocked(d.day);
                      const isCompleted = isDayCompleted(d.day);
                      const isSelected = selectedDay === d.day;
                      
                      return (
                        <button
                          key={d.day}
                          onClick={() => isUnlocked && setSelectedDay(d.day)}
                          disabled={!isUnlocked}
                          className={`
                            w-9 h-9 rounded-lg flex items-center justify-center text-sm font-medium
                            transition-all duration-200 shadow-sm
                            ${isSelected 
                              ? `bg-gradient-to-br ${wTheme.accentGradient} text-white shadow-md` 
                              : isCompleted 
                                ? `bg-white/80 text-[${wTheme.color}]` 
                                : isUnlocked 
                                  ? 'bg-white text-[#57534E] hover:bg-white/90' 
                                  : 'bg-white/40 text-[#A8A29E] cursor-not-allowed'
                            }
                          `}
                          style={isCompleted && !isSelected ? { color: wTheme.color } : {}}
                          data-testid={`day-selector-${d.day}`}
                        >
                          {isCompleted ? <Check size={14} /> : d.day}
                        </button>
                      );
                    })}
                </div>
              );
            })}
          </div>
          
          {/* Week labels */}
          <div className="flex gap-3 mt-2 px-1">
            {[1, 2, 3].map(week => (
              <div key={week} className="flex-1 text-center">
                <span className="text-[10px] text-[#A8A29E] uppercase tracking-wider">
                  Week {week}
                </span>
              </div>
            ))}
          </div>
        </div>
        
        {/* Day Content */}
        <AnimatePresence mode="wait">
          {dayContent && (
            <motion.div
              key={selectedDay}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
            >
              {/* Week Theme Badge */}
              <div 
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full mb-4"
                style={{ backgroundColor: weekTheme.lightBg }}
              >
                <div 
                  className="w-2 h-2 rounded-full"
                  style={{ backgroundColor: weekTheme.color }}
                />
                <span 
                  className="text-xs font-medium"
                  style={{ color: weekTheme.color }}
                >
                  Week {dayContent.week} · {dayContent.theme}
                </span>
              </div>
              
              {/* Day Title */}
              <h2 className="text-2xl font-bold text-[#292524] mb-6">
                Day {dayContent.day}: {dayContent.title}
              </h2>
              
              {/* Opening Insight - Week colored */}
              {dayContent.opening_insight && (
                <CollapsibleSection 
                  id="insight" 
                  icon={Lightbulb} 
                  title="Today's Insight" 
                  color={weekTheme.color}
                  bgGradient={weekTheme.gradient}
                >
                  <p className="text-[#57534E] leading-relaxed italic">
                    "{dayContent.opening_insight}"
                  </p>
                </CollapsibleSection>
              )}
              
              {/* Practice Steps - Week colored */}
              {dayContent.practice_steps && (
                <CollapsibleSection 
                  id="practice" 
                  icon={Heart} 
                  title="Today's Practice" 
                  color={weekTheme.color}
                  bgGradient={weekTheme.gradient}
                >
                  <div className="space-y-3">
                    {dayContent.practice_steps.map((step, i) => (
                      <div key={i} className="flex gap-3">
                        <div 
                          className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-medium"
                          style={{ backgroundColor: weekTheme.lightBg, color: weekTheme.color }}
                        >
                          {i + 1}
                        </div>
                        <p className="text-[#292524] pt-0.5">{step}</p>
                      </div>
                    ))}
                  </div>
                  
                  {/* Breathing Reset Link */}
                  <div className="mt-4 pt-4 border-t border-[#E6E2DD]">
                    <p className="text-sm text-[#78716C] mb-3">
                      If you need support with breathing, use our guided tool:
                    </p>
                    <Button
                      variant="outline"
                      onClick={() => navigate("/breathing-reset")}
                      className="w-full border-[#4A6741]/30 text-[#4A6741] hover:bg-[#4A6741]/5"
                      data-testid="open-breathing-reset-btn"
                    >
                      <Wind size={16} className="mr-2" />
                      Open Breathing Reset
                    </Button>
                  </div>
                </CollapsibleSection>
              )}
              
              {/* Connection Tip - Warm amber gradient */}
              {dayContent.connection_tip && (
                <CollapsibleSection 
                  id="connection" 
                  icon={MessageCircle} 
                  title="Connection Tip" 
                  color="#D4A373"
                  bgGradient="from-amber-50 via-orange-50 to-yellow-50"
                >
                  <p className="text-[#57534E]">{dayContent.connection_tip}</p>
                </CollapsibleSection>
              )}
              
              {/* Daily Affirmations - Warm violet gradient */}
              {dayContent.affirmations && (
                <CollapsibleSection 
                  id="affirmations" 
                  icon={Sparkles} 
                  title="Daily Affirmations" 
                  color="#8B5CF6"
                  bgGradient="from-violet-50 via-purple-50 to-fuchsia-50"
                >
                  <div className="space-y-3 mb-4">
                    {dayContent.affirmations.map((affirmation, i) => (
                      <div 
                        key={i} 
                        className="p-4 rounded-xl bg-white/70 border border-violet-100 shadow-sm"
                      >
                        <p className="text-[#292524] text-center font-medium">
                          "{affirmation}"
                        </p>
                      </div>
                    ))}
                  </div>
                  
                  <div className="pt-3 border-t border-violet-100/50">
                    <p className="text-sm text-[#78716C] mb-3">
                      Record these in your own voice for a more powerful experience:
                    </p>
                    <Button
                      variant="outline"
                      onClick={() => navigate("/studio")}
                      className="w-full border-violet-300 text-violet-700 hover:bg-violet-100/50 bg-white/60"
                      data-testid="open-voice-studio-btn"
                    >
                      <Mic size={16} className="mr-2" />
                      Record in Voice Studio
                    </Button>
                  </div>
                </CollapsibleSection>
              )}
              
              {/* Science Snippet */}
              {dayContent.science_snippet && (
                <CollapsibleSection id="science" icon={Lightbulb} title="Why This Works" color="#78716C">
                  <p className="text-[#57534E] text-sm">
                    {dayContent.science_snippet}
                  </p>
                </CollapsibleSection>
              )}
              
              {/* Evening Reflection */}
              {dayContent.evening_reflection && (
                <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl p-5 border border-indigo-100 mb-6">
                  <div className="flex items-center gap-2 mb-2">
                    <Moon size={16} className="text-indigo-500" />
                    <span className="text-sm font-medium text-indigo-700">Evening Reflection</span>
                  </div>
                  <p className="text-[#57534E] italic">
                    {dayContent.evening_reflection}
                  </p>
                </div>
              )}
              
              {/* Main Reflection Input */}
              {!isDayCompleted(selectedDay) && isDayUnlocked(selectedDay) && (
                <div className="bg-white rounded-2xl shadow-sm border border-[#E6E2DD] p-6 mb-6">
                  <h3 className="text-sm font-medium text-[#78716C] uppercase tracking-wider mb-3">
                    Your Reflection
                  </h3>
                  <p className="text-[#57534E] mb-4 italic">
                    {dayContent.reflection_prompt}
                  </p>
                  <textarea
                    value={reflection}
                    onChange={(e) => setReflection(e.target.value)}
                    placeholder="Take a moment to reflect... (optional)"
                    className="w-full p-4 rounded-xl border border-[#E6E2DD] bg-[#F9FAFB] text-[#292524] placeholder:text-[#A8A29E] resize-none focus:outline-none focus:ring-2 focus:ring-[#4A6741]/20 focus:border-[#4A6741]"
                    rows={4}
                    data-testid="reflection-textarea"
                  />
                </div>
              )}
              
              {/* Saved Reflection */}
              {isDayCompleted(selectedDay) && progress?.reflections?.[selectedDay] && (
                <div className="bg-[#4A6741]/5 rounded-2xl p-6 mb-6 border border-[#4A6741]/10">
                  <h3 className="text-sm font-medium text-[#4A6741] uppercase tracking-wider mb-3">
                    Your Reflection
                  </h3>
                  <p className="text-[#57534E] italic">
                    "{progress.reflections[selectedDay].text}"
                  </p>
                </div>
              )}
              
              {/* Complete Button - Week colored */}
              {!isDayCompleted(selectedDay) && isDayUnlocked(selectedDay) && (
                <Button
                  onClick={handleCompleteDay}
                  disabled={completing}
                  className={`w-full py-4 text-white rounded-xl bg-gradient-to-r ${weekTheme.accentGradient} hover:opacity-90 transition-opacity`}
                  data-testid="complete-day-btn"
                >
                  {completing ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Check className="w-5 h-5 mr-2" />
                      Complete Day {selectedDay}
                    </>
                  )}
                </Button>
              )}
              
              {/* Completed State - Week colored */}
              {isDayCompleted(selectedDay) && (
                <div 
                  className="flex items-center justify-center gap-2 py-4"
                  style={{ color: weekTheme.color }}
                >
                  <Check className="w-5 h-5" />
                  <span className="font-medium">Day Completed</span>
                </div>
              )}
              
              {/* Next Day Button */}
              {isDayCompleted(selectedDay) && selectedDay < 21 && (
                <button
                  onClick={() => setSelectedDay(selectedDay + 1)}
                  className="w-full mt-4 flex items-center justify-between p-4 rounded-xl bg-white border border-[#E6E2DD] hover:border-[#4A6741]/30 transition-colors"
                  data-testid="next-day-btn"
                >
                  <span className="text-[#57534E]">Continue to Day {selectedDay + 1}</span>
                  <ChevronRight className="w-5 h-5 text-[#78716C]" />
                </button>
              )}
            </motion.div>
          )}
        </AnimatePresence>
        
        {/* Footer Quote */}
        <div className="mt-12 text-center">
          <p className="text-sm text-[#A8A29E] italic">
            "Calm parents create calm children. Connection grows through presence."
          </p>
        </div>
      </div>
    </div>
  );
};

export default ParentChallengePage;
